import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  Box,
  InputAdornment,
  TextField,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  ListItemText,
  Badge,
  Collapse,
  Popover,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import Button from "../Button/Button";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterBadge from "@mui/icons-material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import Filter from "../Filter/Filter.js";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import EditableRow from "./EditableRow";
import SaveIcon from "@mui/icons-material/Save";

import {
  StyledTableCell,
  TableToolbar,
  SearchField,
  ActionButton,
  StyledTableRow,
  HeaderSearchInput,
  TableTitle,
  tableColors,
  SortIconWrapper,
  TableHeader,
  TableHeaderCell,
  ArrowDropDown,
  DataCell,
  FilterContainer,
  FilterButton,
  FilterPopoverContent,
  FilterHeader,
  FilterOptions,
  TableWrapper,
  SearchAndFilterContainer,
  FilterActions,
  FilterCount,
  ClearAllButton,
} from "./TableStyles";
import DatePicker from "../DatePicker/DatePicker.js";
import MilestonePublish from "../../views/Project/ProjectMilestone/MilestonePublish.js";
import { useRbac } from "../../hooks/RbacContext.js";

const getColumnWidth = (columnId) => {
  const widths = {
    actions: 100, // Width for actions column
    checkbox: 48, // Width for selection checkbox
    expand: 48, // Width for expand/collapse button
  };
  return widths[columnId] || 200; // Default width of 200 for undefined columns
};

const getFixedPosition = (
  columnId,
  fixedColumns,
  allColumns,
  showSelection,
  showExpand
) => {
  const { left, right } = fixedColumns;

  if (left.includes(columnId)) {
    let position = 0;
    // Add selection checkbox width if it's shown
    if (showSelection) {
      position += 48;
    }
    // Add expand button width if it's shown
    if (showExpand) {
      position += 48;
    }
    // Calculate position based on previous columns
    for (let i = 0; i < left.indexOf(columnId); i++) {
      position += getColumnWidth(left[i]);
    }
    return { position: "left", value: position };
  }

  if (right.includes(columnId)) {
    let position = 0;
    for (let i = right.length - 1; i > right.indexOf(columnId); i--) {
      position += getColumnWidth(right[i]);
    }
    return { position: "right", value: position };
  }

  return { position: null, value: null };
};

const getSearchWidth = (value) => {
  const baseWidth = 300;
  const charWidth = 8; // Approximate width per character
  const padding = 64; // Account for icons and padding
  const calculatedWidth = value.length * charWidth + padding;
  return Math.max(baseWidth, Math.min(600, calculatedWidth)); // Between 300 and 600px
};

const CustomTable = ({
  columns,
  data,
  onEdit,
  onDelete,
  onAdd,
  showActions = true,
  showSubActions = false,
  showAddButton = true,
  addButtonDisable = false,
  setAddButtonDisable,
  showSubAddButton = false,
  showSearch = true,
  showFilters = true,
  showSelection = false,
  showColumnVisibility = false,
  showExpandableRows = false,
  showDateFilter = false,
  handleDateChangeFilter,
  handleCloseDateChangeFilter,
  handleClearDateFilter,
  minDate = null,
  maxDate = null,
  handleFilterDateClick,
  dateFilterOpen,
  dateRangeValue = [],
  expandedRowRender = null,
  expandableRowColumns = [],
  onRowExpand = null,
  onAddSubRow = null,
  expandedRows = null,
  subTableButton = null,
  fetchExpandData = null,
  pageSize = 10,
  addButtonText = "Add New",
  addButtonIcon = <PersonAddIcon />,
  disabled = false,
  onSelectionChange,
  defaultHiddenColumns = [],
  stickyHeader = true,
  fixedColumns = { left: [], right: [] },
  actions = [],
  subActions = [],
  title = "",
  subtitle = "",
  onGlobalSearch = () => { },
  alignment = "left",
  fontSize = "13px",
  columnFilters = [],
  totalCount,
  serverSidePagination = false,
  hasMore,
  onPageChange,
  onRowsPerPageChange,
  page: externalPage,
  staticFilters = false,
  staticColumnFilters = [],
  staticFilterValues = {},
  onStaticFilterChange,
  onExpandAll = null,
  expandAllLoading = null,
  formFields = {},
  getOptionsForField,
  onRowSave,
  setSelectedTask,
  rowEdit = false,
  onCancelNew,
  isNewRow,
  unavailableResourceIds,
  Searchholder,
  publishMilestoneButton = false,
  onStatusUpdate,
  hasEditAccess = false,
  hasApproveAccess = false,
  projectDetails = null,
  enableRowClickExpand = false
}) => {
  const [internalPage, setInternalPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(pageSize);
  const [selected, setSelected] = React.useState([]);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [visibleColumns, setVisibleColumns] = React.useState(
    columns
      .map((col) => col.id)
      .filter((id) => !defaultHiddenColumns.includes(id))
  );
  const [sortConfig, setSortConfig] = React.useState({
    key: null,
    direction: "asc",
  });
  const [globalSearchValue, setGlobalSearchValue] = React.useState("");
  const [filterValues, setFilterValues] = React.useState({});
  const [activeFilter, setActiveFilter] = React.useState(null);
  const [editingRow, setEditingRow] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {permissions} = useRbac();

  // Add null safety for expandedRows
  const safeExpandedRows = expandedRows || {};

  // Use internal or external page based on pagination type
  const currentPage = serverSidePagination ? externalPage : internalPage;

  const handleChangePage = (event, newPage) => {
    if (serverSidePagination) {
      onPageChange?.(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    if (!serverSidePagination) {
      setInternalPage(0);
    }
    onRowsPerPageChange?.(event);
  };

  const handleGlobalSearch = (event) => {
    const value = event.target.value;
    setGlobalSearchValue(value);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = data.map((row) => row.id);
      setSelected(newSelected);
      onSelectionChange?.(newSelected);
    } else {
      setSelected([]);
      onSelectionChange?.([]);
    }
  };

  const handleRowSelect = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
    onSelectionChange?.(newSelected);
  };

  const handleSort = (columnId) => {
    setSortConfig((prev) => {
      const newConfig = {
        key: columnId,
        direction:
          prev.key === columnId && prev.direction === "asc" ? "desc" : "asc",
      };
      return newConfig;
    });
  };

  const handleFilterChange = (columnId, values) => {
    setFilterValues((prev) => ({
      ...prev,
      [columnId]: values,
    }));
  };

  // Toggle row expansion
  const toggleRowExpansion = (row) => {
    onRowExpand(row, !safeExpandedRows[row.task_id]);
  };

  const getUniqueColumnValues = (columnId) => {
    const filter = columnFilters.find((f) => f.id === columnId);

    const currentFilterValues = { ...filterValues };
    delete currentFilterValues[columnId];

    let dataToFilter = data;
    const activeFilters = Object.entries(currentFilterValues).filter(
      ([_, values]) => values?.length > 0
    );

    if (activeFilters.length > 0) {
      dataToFilter = dataToFilter.filter((row) => {
        return activeFilters.every(([filterId, values]) => {
          const columnFilter = columnFilters.find((f) => f.id === filterId);
          const rowValue = columnFilter?.render
            ? columnFilter.render(row[filterId])
            : row[filterId];
          return values.includes(rowValue);
        });
      });
    }

    return [
      ...new Set(
        dataToFilter.map((item) => {
          const value = item[columnId];
          return filter?.render ? filter.render(value) : value;
        })
      ),
    ]
      .filter(Boolean)
      .sort();
  };

  const filteredData = useMemo(() => {
    let filtered = data;

    // Apply global search first
    if (globalSearchValue) {
      const searchTerm = globalSearchValue.toLowerCase().trim();
      if (searchTerm) {
        filtered = filtered.filter((row) => {
          return columns
            .filter((column) => visibleColumns.includes(column.id))
            .some((column) => {
              let value;

              // Use searchValue function if provided
              if (column.searchValue) {
                value = column.searchValue(row[column.id], row);
              }
              // Use render function if provided
              else if (column.render) {
                const renderedValue = column.render(row[column.id], row);
                if (React.isValidElement(renderedValue)) {
                  // Extract text from React elements recursively
                  const extractText = (element) => {
                    if (!element) return "";
                    if (typeof element === "string") return element;
                    if (Array.isArray(element))
                      return element.map(extractText).join(" ");
                    if (React.isValidElement(element)) {
                      return extractText(element.props.children);
                    }
                    return String(element);
                  };
                  value = extractText(renderedValue);
                } else {
                  value = String(renderedValue || "");
                }
              }
              // Fallback to raw value
              else {
                value = row[column.id];
              }

              const stringValue = String(value || "").toLowerCase();

              if (visibleColumns.includes(column.id)) {
                return stringValue.includes(searchTerm);
              }
              return false;
            });
        });
      }
    }

    // Then apply column filters
    const activeFilters = Object.entries(filterValues).filter(
      ([_, values]) => values?.length > 0
    );

    if (activeFilters.length > 0) {
      filtered = filtered.filter((row) => {
        return activeFilters.every(([columnId, values]) => {
          const filter = columnFilters.find((f) => f.id === columnId);
          const column = columns.find((col) => col.id === columnId);

          // Special handling for array type filters
          if (Array.isArray(row[columnId])) {

            // Check if any of the array items match the filter values
            const hasMatch = row[columnId]?.some(item => {
              // Get the value using the column's searchValue function if available
              const itemValue = column?.searchValue?.(item, row) || item;

              return values.some(filterValue =>
                String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase())
              );
            });

            return hasMatch;
          }

          // Default handling for non-array values
          const rowValue = filter?.render
            ? filter.render(row[columnId])
            : row[columnId];
          return values.includes(rowValue);
        });
      });
    }

    return filtered;
  }, [
    data,
    filterValues,
    globalSearchValue,
    columnFilters,
    columns,
    visibleColumns,
  ]);

  const sortedData = React.useMemo(() => {
    console.log("Starting sort with config:", sortConfig);
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const column = columns.find((col) => col.id === sortConfig.key);
      console.log("Sorting column:", column?.label);

      const getDisplayValue = (value, row) => {
        // First check for custom sortValue function
        if (column?.sortValue) {
          const sortValue = column.sortValue(value, row);
          console.log("Using custom sortValue:", sortValue);
          return sortValue;
        }

        if (column?.searchValue) {
          const searchValue = column.searchValue(value, row);
          console.log("Using searchValue:", searchValue);
          return searchValue;
        }

        if (!column?.render) {
          if (typeof value === "object" && value !== null) {
            if (value.reporting_manager) {
              const managerName = `${value.reporting_manager.first_name || ""
                } ${value.reporting_manager.last_name || ""}`.trim();
              console.log("Using manager name:", managerName);
              return managerName;
            }
            const objValues = Object.values(value)
              .filter((v) => v !== null && v !== undefined)
              .join(" ");
            console.log("Using object values:", objValues);
            return objValues;
          }
          console.log("Using raw value:", value);
          return value;
        }

        const renderedValue = column.render(value, row);
        console.log("Rendered value:", renderedValue);
        return renderedValue;
      };

      const aValue = getDisplayValue(a[sortConfig.key], a);
      const bValue = getDisplayValue(b[sortConfig.key], b);
      console.log("Comparing values:", {
        aValue,
        bValue,
        direction: sortConfig.direction,
      });

      // Handle null/undefined values - put them at the end
      if (aValue === null || aValue === undefined) {
        console.log("A value is null/undefined, moving to end");
        return 1;
      }
      if (bValue === null || bValue === undefined) {
        console.log("B value is null/undefined, moving to end");
        return -1;
      }

      // Handle Date objects
      if (aValue instanceof Date && bValue instanceof Date) {
        console.log("Comparing Date objects:", { aValue, bValue });
        const result =
          sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
        console.log("Date object comparison result:", result);
        return result;
      }

      // Handle string dates in format "DD MMM YYYY"
      const dateRegex =
        /^\d{2}\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|SEPT|OCT|NOV|DEC|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+\d{4}$/;
      const isDateFormat = dateRegex.test(aValue) && dateRegex.test(bValue);

      if (isDateFormat) {
        console.log("Values are date strings, using date comparison");
        const parseCustomDate = (dateStr) => {
          const [day, month, year] = dateStr.split(" ");
          const monthMap = {
            JAN: 0,
            FEB: 1,
            MAR: 2,
            APR: 3,
            MAY: 4,
            JUN: 5,
            JUL: 6,
            AUG: 7,
            SEP: 8,
            SEPT: 8,
            OCT: 9,
            NOV: 10,
            DEC: 11,
            Jan: 0,
            Feb: 1,
            Mar: 2,
            Apr: 3,
            May: 4,
            Jun: 5,
            Jul: 6,
            Aug: 7,
            Sep: 8,
            Sept: 8,
            Oct: 9,
            Nov: 10,
            Dec: 11,
          };
          // Create date with UTC to avoid timezone issues
          return new Date(
            Date.UTC(
              parseInt(year),
              monthMap[month.toUpperCase()],
              parseInt(day)
            )
          );
        };

        const dateA = parseCustomDate(aValue);
        const dateB = parseCustomDate(bValue);
        console.log("Parsed dates:", { dateA, dateB });

        // Compare timestamps to ensure consistent sorting
        const timestampA = dateA.getTime();
        const timestampB = dateB.getTime();
        console.log("Date timestamps:", { timestampA, timestampB });

        const result =
          sortConfig.direction === "asc"
            ? timestampA - timestampB
            : timestampB - timestampA;
        console.log("Date string comparison result:", result);
        return result;
      }

      // Handle numbers
      const aNum = Number(aValue);
      const bNum = Number(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        const result =
          sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
        console.log("Number comparison result:", result);
        return result;
      }

      // Default to string comparison
      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );
      const result = sortConfig.direction === "asc" ? comparison : -comparison;
      console.log("String comparison result:", result);
      return result;
    });
  }, [filteredData, sortConfig, columns]);

  const getColumnSx = (columnId, isHeader = false) => {
    const { position, value } = getFixedPosition(
      columnId,
      fixedColumns,
      columns,
      showSelection,
      showExpandableRows
    );

    if (!position) return {};

    return (theme) => ({
      position: "sticky",
      [position]: value,
      zIndex:
        position === "left" ? (isHeader ? 1200 : 1100) : isHeader ? 1000 : 900,
      backgroundColor: isHeader
        ? tableColors.header.background
        : tableColors.row.even,
    });
  };
   const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
    ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
        parseInt(id.trim())
      )
    : [];

  const isSuperAdmin = superAdminRoleIds.includes(
    parseInt(permissions?.globalRole?.role_id)
  );

  const getSelectionColumnSx = (isHeader) => (theme) => ({
    position: "sticky",
    left: showExpandableRows ? 48 : 0,
    zIndex: isHeader ? 3 : 2,
    backgroundColor: isHeader
      ? tableColors.header.background
      : tableColors.row.even,
    width: 48,
    minWidth: 48,
  });

  const getExpandColumnSx = (isHeader) => (theme) => ({
    position: "sticky",
    left: 0,
    zIndex: isHeader ? 3 : 2,
    backgroundColor: isHeader
      ? tableColors.header.background
      : tableColors.row.even,
    width: 48,
    minWidth: 48,
  });

  const getActionsColumnSx = (isHeader) => (theme) => ({
    position: "sticky",
    right: 0,
    zIndex: isHeader ? 3 : 2,
    backgroundColor: isHeader
      ? tableColors.header.background
      : tableColors.row.even,
    width: 100,
    minWidth: 100,
    fontSize: "10.8px",
  });

  const renderTableToolbarWithoutSearch = () => (
    <TableToolbar>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 2,
        }}
      >
        {title && (
          <Box sx={{ flex: 1 }}>
            <TableTitle>{title}</TableTitle>
            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </TableToolbar>
  );

  const renderTableToolbarWithFilters = () => (
    <SearchAndFilterContainer>
      {showSearch && (
        <SearchField
          placeholder={Searchholder ? Searchholder : "Search..."}
          value={globalSearchValue}
          onChange={handleGlobalSearch}
          fullWidth={isMobile}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: globalSearchValue && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setGlobalSearchValue("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            style: {
              paddingRight: globalSearchValue ? "0px" : "14px",
            },
          }}
          sx={{
            "& input::placeholder": {
              textOverflow: "ellipsis !important",
              width: "100%",
              overflow: "hidden",
            },
          }}
        />
      )}

      {showFilters && columnFilters.length > 0 && (
        <FilterContainer>
          {columnFilters.map((filter) => {
            const activeFilterValues = filterValues[filter.id] || [];
            const hasActiveFilters = activeFilterValues.length > 0;

            return (
              <FilterButton
                key={filter.id}
                active={activeFilter === filter.id}
                hasFilters={hasActiveFilters}
                onClick={() =>
                  setActiveFilter(activeFilter === filter.id ? null : filter.id)
                }
                aria-expanded={activeFilter === filter.id}
                aria-haspopup="true"
                data-filter-id={filter.id}
              >
                {filter.icon && (
                  <span className="filter-icon">{filter.icon}</span>
                )}
                <span>{filter.label}</span>
                {hasActiveFilters && (
                  <>
                    <FilterCount>{activeFilterValues.length}</FilterCount>
                    <CloseIcon
                      className="clear-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilterValues((prev) => ({
                          ...prev,
                          [filter.id]: [],
                        }));
                      }}
                    />
                  </>
                )}
                <KeyboardArrowDownIcon className="send-icon" />
              </FilterButton>
            );
          })}

          {/* Add Clear All button if any filters are active */}
          {Object.values(filterValues).some((values) => values.length > 0) && (
            <ClearAllButton
              variant="outlined"
              onClick={() => setFilterValues({})}
              startIcon={<CloseIcon />}
            >
              Clear All
            </ClearAllButton>
          )}
        </FilterContainer>
      )}
    </SearchAndFilterContainer>
  );

  const renderActions = (row, subAction = false) => {
    if (!showActions) return null;

    const actionItems = subAction
      ? subActions(row)
      : typeof actions === "function"
        ? actions(row)
        : actions;
    const isNewRecord = row.task_id?.toString().startsWith("temp-");

    // Check if dates are within milestone range
    const isDateOutOfRange = () => {
      const milestoneOptions = getOptionsForField?.("milestones") || [];
      const selectedMilestone = milestoneOptions.find(
        (m) => m.value.toString() === row.milestone_id?.toString()
      );

      if (!selectedMilestone || !row.start_date || !row.end_date) return true;

      const milestoneStart = new Date(selectedMilestone.milestone_start_date);
      const milestoneEnd = new Date(selectedMilestone.milestone_end_date);
      const taskStart = new Date(row.start_date);
      const taskEnd = new Date(row.end_date);

      // Set all dates to start of day for proper comparison
      [milestoneStart, milestoneEnd, taskStart, taskEnd].forEach((date) => {
        date.setHours(0, 0, 0, 0);
      });

      return taskStart < milestoneStart || taskEnd > milestoneEnd;
    };

    const datesOutOfRange = isDateOutOfRange();

    return (
      <StyledTableCell
        align="center"
        data-field="actions"
        sx={getActionsColumnSx(false)}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {isNewRecord || editingRow === row.task_id ? (
            <>
              <IconButton
                size="small"
                onClick={() => {
                  const updatedData = {
                    ...row,
                    isNew: isNewRecord,
                  };
                  onRowSave?.(updatedData);
                  setEditingRow(null);
                }}
                sx={{
                  color: "success.main",
                  "&:hover": {
                    backgroundColor: "success.lighter",
                  },
                  "&.Mui-disabled": {
                    color: "success.main",
                    opacity: 0.5,
                  },
                }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  if (isNewRecord) {
                    const updatedData = data.filter(
                      (item) => item.task_id !== row.task_id
                    );
                    onCancelNew?.(updatedData);
                  } else {
                    setEditingRow(null);
                  }
                }}
                sx={{
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "error.lighter",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              {actionItems?.map((action, index) => (
                <Tooltip key={index} title={action.label}>
                  <IconButton
                    size="small"
                    onClick={() => action.onClick(row)}
                    disabled={!!action.disabled}
                    sx={{
                      color:
                        action.color === "error"
                          ? "error.main"
                          : "primary.main",
                      padding: "4px",
                      ...action.sx,
                    }}
                  >
                    {action.icon}
                  </IconButton>
                </Tooltip>
              ))}
              {rowEdit && (
                <>
                  {/* {datesOutOfRange && (
                    <Tooltip title="Activity dates are outside the milestone date range">
                      <IconButton
                        size="small"
                        sx={{
                          color: (theme) => theme.palette.warning.main,
                          padding: "4px",
                        }}
                      >
                        <WarningIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )} */}
                  {hasApproveAccess && (
                    <IconButton
                      size="small"
                      onClick={() => onStatusUpdate?.(row)}
                      disabled={row.task_status_code === "COMPLETED"}
                      sx={{
                        color: (theme) =>
                          row.task_status_code === "COMPLETED"
                            ? theme.palette.success.main
                            : theme.palette.grey[400],
                        padding: "4px",
                        "&:hover": {
                          backgroundColor: (theme) =>
                            row.task_status_code === "COMPLETED"
                              ? theme.palette.success.lighter
                              : theme.palette.grey[100],
                        },
                        "&.Mui-disabled": {
                          color: (theme) =>
                            row.task_status_code === "COMPLETED"
                              ? theme.palette.success.main
                              : theme.palette.grey[400],
                        },
                      }}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  )}
                  {hasEditAccess && (row.task_status_code !== "COMPLETED" || isSuperAdmin) && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedTask(row);
                          setEditingRow(row.task_id);
                        }}
                        sx={{
                          color: (theme) => theme.palette.primary.main,
                          padding: "4px",
                        }}
                        disabled={addButtonDisable}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDelete?.(row)}
                        sx={{
                          color: (theme) => theme.palette.error.main,
                          padding: "4px",
                        }}
                        disabled={addButtonDisable}

                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </StyledTableCell>
    );
  };

  // Reset page to 0 when search or filters change
  React.useEffect(() => {
    if (!serverSidePagination) {
      setInternalPage(0);
    }
  }, [globalSearchValue, filterValues, serverSidePagination]);

  // Calculate pagination count based on filtered data
  const paginationCount = React.useMemo(() => {
    if (serverSidePagination) {
      return totalCount;
    }
    return sortedData.length;
  }, [serverSidePagination, totalCount, sortedData]);

  // Get paginated data for client-side pagination
  const paginatedData = React.useMemo(() => {
    if (serverSidePagination) {
      return sortedData;
    }
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [serverSidePagination, sortedData, currentPage, rowsPerPage]);

  const handleToggle = (value) => {
    const currentValues = filterValues[activeFilter] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];

    setFilterValues((prev) => ({
      ...prev,
      [activeFilter]: newValues,
    }));
  };

  const renderExpandableRowContent = (row) => {
    if (!expandedRowRender) return null;

    const expandableContent = expandedRowRender(row);

    const colSpan =
      (showSelection ? 1 : 0) +
      (showExpandableRows ? 1 : 0) +
      visibleColumns.length +
      (showActions ? 1 : 0);

    let contentToRender;
    // If expandedRowRender returns a valid React element, render it directly.
    if (React.isValidElement(expandableContent)) {
      contentToRender = expandableContent;
    } else {
      // Fallback to original behavior: rendering a sub-table from an array of data.
      const expandableData = expandableContent;
      contentToRender = (
        <Box sx={{ margin: 2 }}>
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {expandableRowColumns.map((column) => (
                    <StyledTableCell
                      key={column.id}
                      align={alignment}
                      fontSize={fontSize}
                      data-field={column.id}
                      column={column}
                      sx={getColumnSx(column.id, true)}
                    >
                      <div className="header-content">{column.label}</div>
                    </StyledTableCell>
                  ))}
                  {showSubActions && (
                    <StyledTableCell align="center" sx={getActionsColumnSx(true)}>
                      ACTIONS
                    </StyledTableCell>
                  )}
                  <StyledTableCell align="center">
                    {showSubAddButton && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => onAddSubRow(row)}
                        size="small"
                        disabled={row?.milestone_status_code === "COMPLETE"}
                      >
                        {subTableButton}
                      </Button>
                    )}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expandableData && expandableData.length > 0 ? (
                  expandableData.map((subRow, subIndex) => (
                    <StyledTableRow key={subRow.id || subIndex}>
                      {expandableRowColumns.map((column) => (
                        <DataCell key={column.id} align={alignment} fontSize={fontSize}>
                          <div className="cell-content">
                            {column.render
                              ? column.render(subRow[column.id], subRow)
                              : subRow[column.id]}
                          </div>
                        </DataCell>
                      ))}
                      {showSubActions && (
                        <StyledTableCell
                          align="center"
                          data-field="actions"
                          sx={getActionsColumnSx(false)}
                        >
                          {renderActions(subRow, true)}
                        </StyledTableCell>
                      )}
                      <TableCell align="center">
                        {/* Customize actions for sub-rows if needed */}
                      </TableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={expandableRowColumns.length + 1}
                      align="center"
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    }

    return (
      <TableRow>
        <TableCell
          colSpan={colSpan}
          sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: 0 }}
        >
          <Collapse
            in={safeExpandedRows[row.task_id]}
            timeout="auto"
            unmountOnExit
          >
            {contentToRender}
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderTableBody = () => {
    if (!data) return null;

    return (
      <TableBody>
        {paginatedData.map((row, index) => {
          const isNewRecord = row.task_id?.toString().startsWith("temp-");
          const isExpanded = safeExpandedRows[row.task_id] || false;
          return (
            <React.Fragment key={row.task_id || index}>
              <StyledTableRow
                hover={!isExpanded}
                selected={selected.includes(row.task_id)}
                sx={isExpanded ? {
                  backgroundColor: (theme) => tableColors.header.background(theme),
                  color: (theme) => tableColors.header.text(theme),
                  "& .MuiTableCell-root": {
                    backgroundColor: (theme) => tableColors.header.background(theme),
                    color: (theme) => tableColors.header.text(theme),
                  },
                  "&:hover": {
                    backgroundColor: (theme) => `${tableColors.header.background(theme)} !important`,
                    "& .MuiTableCell-root": {
                      backgroundColor: (theme) => `${tableColors.header.background(theme)} !important`,
                      color: (theme) => `${tableColors.header.text(theme)} !important`,
                    },
                  }
                } : undefined}
              >
                {showExpandableRows && (
                  <TableCell padding="checkbox" sx={getExpandColumnSx(false)}>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(row)}
                      aria-label="expand row"
                      sx={{
                        // Style the expand/collapse icon for expanded rows
                        color: isExpanded ? (theme) => tableColors.header.text(theme) : "inherit",
                        backgroundColor: isExpanded ? (theme) => tableColors.header.background(theme) : "inherit",
                      }}
                    >
                      {safeExpandedRows[row.task_id] ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                )}
                {showSelection && (
                  <TableCell
                    padding="checkbox"
                    sx={getSelectionColumnSx(false)}
                  >
                    <Checkbox
                      checked={selected.includes(row.task_id)}
                      onChange={() => handleRowSelect(row.task_id)}
                      disabled={disabled}
                    />
                  </TableCell>
                )}
                {(isNewRecord || editingRow === row.task_id) && rowEdit ? (
                  <EditableRow
                    row={row}
                    columns={columns.filter((column) =>
                      visibleColumns.includes(column.id)
                    )}
                    formFields={formFields}
                    onSave={(updatedData) => {
                      onRowSave?.(updatedData);
                      setEditingRow(null);
                    }}
                    onCancel={() => {
                      if (isNewRecord) {
                        const updatedData = data.filter(
                          (item) => item.task_id !== row.task_id
                        );
                        onCancelNew?.(updatedData);
                      }
                      setEditingRow(null);
                    }}
                    getOptionsForField={getOptionsForField}
                    alignment={alignment}
                    fontSize={fontSize}
                    unavailableResourceIds={unavailableResourceIds}
                    projectDetails={projectDetails}
                  />
                ) : (
                  <>
                    {columns
                      .filter((column) => visibleColumns.includes(column.id))
                      .map((column) => (
                        <DataCell
                          key={column.id}
                          align={alignment}
                          fontSize={fontSize}
                          data-field={column.id}
                          data-label={column.label}
                          column={column}
                          sx={getColumnSx(column.id, false)}
                          onClick={
                            enableRowClickExpand && column.id !== 'expand' && showExpandableRows
                              ? () => toggleRowExpansion(row)
                              : undefined
                          }
                        >
                          <div className="cell-content">
                            {column.render
                              ? column.render(row[column.id], row)
                              : row[column.id]}
                          </div>
                        </DataCell>
                      ))}
                    {renderActions(row)}
                  </>
                )}
              </StyledTableRow>
              {showExpandableRows && renderExpandableRowContent(row)}
            </React.Fragment>
          );
        })}
      </TableBody>
    );
  };

  const handleAddClick = () => {
    setInternalPage(0);
    onAdd && onAdd();
  };

  return (
    <TableWrapper enableRowClickExpand={enableRowClickExpand}>
      {/* Title and Add Button Section */}
      <TableToolbar>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 2,
          }}
        >
          {title && (
            <Box sx={{ flex: 1 }}>
              <TableTitle>{title}</TableTitle>
              {subtitle && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            {showExpandableRows && (
              <Button
                variant="outlined"
                onClick={() =>
                  onExpandAll?.(!Object.values(safeExpandedRows).some(Boolean))
                }
                disabled={expandAllLoading}
                startIcon={
                  expandAllLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : Object.values(safeExpandedRows).some(Boolean) ? (
                    <UnfoldLessIcon />
                  ) : (
                    <UnfoldMoreIcon />
                  )
                }
                size="small"
              >
                {expandAllLoading
                  ? "Loading..."
                  : Object.values(safeExpandedRows).some(Boolean)
                    ? "Hide Activities"
                    : "Show Activities"}
              </Button>
            )}
            {showDateFilter && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterDateClick}
                  size="small"
                  endIcon={
                    dateRangeValue[0] !== null && dateRangeValue[1] !== null ? (
                      <ClearIcon
                        fontSize="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearDateFilter();
                        }}
                      />
                    ) : null
                  }
                >
                  Time Period
                </Button>
                <Popover
                  open={Boolean(dateFilterOpen)}
                  anchorEl={dateFilterOpen}
                  onClose={handleCloseDateChangeFilter}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <DatePicker
                    mode="range"
                    value={dateRangeValue}
                    onChange={handleDateChangeFilter}
                    onClose={handleCloseDateChangeFilter}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                </Popover>
              </>
            )}
            {showAddButton && (
              <Button
                variant="contained"
                onClick={handleAddClick}
                startIcon={
                  typeof addButtonIcon === "string" ? (
                    <img
                      src={addButtonIcon}
                      alt="Button Icon"
                      style={{ width: 17, height: 17 }}
                    />
                  ) : (
                    addButtonIcon // FOr MUI icon component
                  )
                }
                disabled={addButtonDisable}
                size="small"
              >
                {addButtonText}
              </Button>
            )}
            {publishMilestoneButton && (
              <MilestonePublish setShowAddButton={setAddButtonDisable} />
            )}
          </Box>
        </Box>
      </TableToolbar>

      {/* Search and Filters Section */}
      {(showSearch || showFilters) && (
        <SearchAndFilterContainer>
          {showSearch && (
            <SearchField
              placeholder={Searchholder ? Searchholder : "Search..."}
              value={globalSearchValue}
              onChange={handleGlobalSearch}
              fullWidth={isMobile}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: globalSearchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setGlobalSearchValue("")}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                style: {
                  paddingRight: globalSearchValue ? "0px" : "14px",
                },
              }}
              sx={{
                "& input::placeholder": {
                  textOverflow: "ellipsis !important",
                  width: "100%",
                  overflow: "hidden",
                },
              }}
            />
          )}

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {showFilters && staticFilters && (
              <Filter
                filters={staticFilterValues}
                columnFilters={staticColumnFilters}
                onFilterChange={onStaticFilterChange}
                isStatic={true}
              />
            )}

            {showFilters && columnFilters.length > 0 && (
              <FilterContainer>
                {columnFilters.map((filter) => {
                  const activeFilterValues = filterValues[filter.id] || [];
                  const hasActiveFilters = activeFilterValues.length > 0;

                  return (
                    <FilterButton
                      key={filter.id}
                      active={activeFilter === filter.id}
                      hasFilters={hasActiveFilters}
                      onClick={() =>
                        setActiveFilter(
                          activeFilter === filter.id ? null : filter.id
                        )
                      }
                      aria-expanded={activeFilter === filter.id}
                      aria-haspopup="true"
                      data-filter-id={filter.id}
                    >
                      {filter.icon && (
                        <span className="filter-icon">{filter.icon}</span>
                      )}
                      <span>{filter.label}</span>
                      {hasActiveFilters && (
                        <>
                          <FilterCount>{activeFilterValues.length}</FilterCount>
                          <CloseIcon
                            className="clear-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterValues((prev) => ({
                                ...prev,
                                [filter.id]: [],
                              }));
                            }}
                          />
                        </>
                      )}
                      <KeyboardArrowDownIcon className="send-icon" />
                    </FilterButton>
                  );
                })}

                {Object.values(filterValues).some(
                  (values) => values.length > 0
                ) && (
                    <ClearAllButton
                      variant="outlined"
                      onClick={() => setFilterValues({})}
                      startIcon={<CloseIcon />}
                    >
                      Clear All
                    </ClearAllButton>
                  )}
              </FilterContainer>
            )}
          </Box>
        </SearchAndFilterContainer>
      )}

      {/* Table Section */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          "& .MuiTableCell-root": {
            borderColor: (theme) => theme.palette.divider,
          },
        }}
      >
        <Table stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {showExpandableRows && (
                <StyledTableCell
                  padding="checkbox"
                  sx={getExpandColumnSx(true)}
                >
                  {/* Empty header for expand/collapse column */}
                </StyledTableCell>
              )}
              {showSelection && (
                <StyledTableCell
                  padding="checkbox"
                  sx={getSelectionColumnSx(true)}
                >
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < sortedData.length
                    }
                    checked={
                      selected.length > 0 &&
                      selected.length === sortedData.length
                    }
                    onChange={handleSelectAllClick}
                    disabled={disabled}
                  />
                </StyledTableCell>
              )}
              {columns
                .filter((column) => visibleColumns.includes(column.id))
                .map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={alignment}
                    fontSize={fontSize}
                    data-field={column.id}
                    column={column}
                    onClick={() => handleSort(column.id)}
                    sx={getColumnSx(column.id, true)}
                  >
                    <div className="header-content">
                      {column.label}
                      <SortIconWrapper
                        active={sortConfig.key === column.id}
                        direction={sortConfig.direction}
                      >
                        <ArrowDropDownIcon style={{ fontSize: "20px" }} />
                      </SortIconWrapper>
                    </div>
                  </StyledTableCell>
                ))}
              {showActions && (
                <StyledTableCell align="center" sx={getActionsColumnSx(true)}>
                  ACTIONS
                </StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          {renderTableBody()}
        </Table>
      </TableContainer>

      {/* Pagination Section */}
      <Box
        sx={{
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <TablePagination
          component="div"
          count={paginationCount}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          nextIconButtonProps={{
            disabled: serverSidePagination
              ? !hasMore
              : (currentPage + 1) * rowsPerPage >= paginationCount,
          }}
          backIconButtonProps={{
            disabled: currentPage === 0,
          }}
          sx={{
            ".MuiTablePagination-toolbar": {
              flexWrap: "wrap",
              gap: 1,
              justifyContent: { xs: "center", sm: "flex-end" },
            },
          }}
        />
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={
          activeFilter
            ? document.querySelector(`[data-filter-id="${activeFilter}"]`)
            : null
        }
        open={Boolean(activeFilter)}
        onClose={() => setActiveFilter(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            mt: 1,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              left: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
      >
        <FilterPopoverContent>
          <FilterHeader>
            <Typography variant="subtitle2">
              Filter by{" "}
              {columnFilters.find((f) => f.id === activeFilter)?.label}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setActiveFilter(null);
              }}
            >
              <ClearIcon />
            </IconButton>
          </FilterHeader>
          <FilterOptions>
            {getUniqueColumnValues(activeFilter).map((value) => {
              console.log(
                "filterValues[activeFilter]?.includes(value)",
                value,
                filterValues[activeFilter]?.includes(value)
              );
              return (
                <MenuItem key={value} dense onClick={() => handleToggle(value)}>
                  <Checkbox
                    checked={
                      filterValues[activeFilter]?.includes(value) || false
                    }
                    onChange={(e) => {
                      // const newValues = e.target.checked
                      //   ? [...(filterValues[activeFilter] || []), value]
                      //   : (filterValues[activeFilter] || []).filter(
                      //       (v) => v !== value
                      //     );
                      // setFilterValues((prev) => ({
                      //   ...prev,
                      //   [activeFilter]: newValues,
                      // }));
                    }}
                    size="small"
                  />
                  <ListItemText primary={value} />
                </MenuItem>
              );
            })}
          </FilterOptions>
        </FilterPopoverContent>
      </Menu>
    </TableWrapper>
  );
};

export default CustomTable;
